import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  CheckCircle,
  Shield,
  Star,
  ArrowRight,
  Calendar,
  Droplets,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Settings,
  Wrench
} from 'lucide-react';
import { SchemaInjector } from '../components/SchemaInjector';

const FaucetPricing = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Real Kitchen Faucet Installation Projects
  const faucetProjects = [
    {
      id: 1,
      image: '/colorado-plumbing-uploads/existing-projects/best-plumbing-company-highlands-ranch.png',
      location: 'Highlands Ranch, CO',
      type: 'High-Grade Kitchen Faucet Installation',
      description: 'Professional installation of premium kitchen faucet with pull-down sprayer and advanced features',
      features: ['High-Grade Installation', 'Pull-Down Sprayer', 'Quality Workmanship']
    },
    {
      id: 2,
      image: '/colorado-plumbing-uploads/existing-projects/trustworthy-plumbers-water-heater-littleton.png',
      location: 'Littleton, CO',
      type: 'Mid-Grade Kitchen Faucet Installation',
      description: 'Complete kitchen faucet installation with enhanced features and professional guarantee',
      features: ['Mid-Grade Quality', 'Enhanced Features', 'Professional Install']
    },
    {
      id: 3,
      image: '/colorado-plumbing-uploads/existing-projects/plumbing-truck-residential-best-plumber-castle-rock.png',
      location: 'Castle Rock, CO',
      type: 'Standard Kitchen Faucet Installation',
      description: 'Reliable standard-grade kitchen faucet installation with professional craftsmanship',
      features: ['Standard Grade', 'Professional Install', 'Quality Guarantee']
    },
    {
      id: 4,
      image: '/colorado-plumbing-uploads/existing-projects/gas-pipe-install-lakewood-colorado-first-plumbing.png',
      location: 'Lakewood, CO',
      type: 'Kitchen Faucet Repair Service',
      description: 'Expert kitchen faucet repair including parts replacement and leak elimination',
      features: ['Expert Repair', 'Parts Included', 'Function Restoration']
    },
    {
      id: 5,
      image: '/colorado-plumbing-uploads/services/residential/remodel-trailer.JPEG',
      location: 'Denver Metro, CO',
      type: 'Kitchen Faucet Supply Line Service',
      description: 'Professional supply line replacement with quality braided stainless steel lines',
      features: ['Supply Line Replacement', 'Quality Materials', 'Leak Prevention']
    }
  ];

  // Unified Schema for the entire page
  const completePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://colorado-plumbing.com/faucet-pricing#webpage",
        "url": "https://colorado-plumbing.com/faucet-pricing",
        "name": "Denver Kitchen Faucet Installation Cost | Labor from $225 | Colorado First Plumbing",
        "description": "See exact, flat-rate labor costs for kitchen faucet installation in Denver. Standard-grade labor: $225. Mid-grade: $325. High-grade: $485. 360 Google Reviews.",
        "isPartOf": { "@id": "https://colorado-plumbing.com/#website" },
        "breadcrumb": { "@id": "https://colorado-plumbing.com/faucet-pricing#breadcrumb" }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://colorado-plumbing.com/faucet-pricing#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://colorado-plumbing.com/" },
          { "@type": "ListItem", "position": 2, "name": "Kitchen Faucet Pricing", "item": "https://colorado-plumbing.com/faucet-pricing" }
        ]
      },
      {
        "@type": "Plumber",
        "@id": "https://colorado-plumbing.com/",
        "name": "Colorado First Plumbing and Sewer LLC",
        "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "5108 Sydney Ave", "addressLocality": "Littleton", "addressRegion": "CO", "postalCode": "80130", "addressCountry": "US"
        },
        "url": "https://colorado-plumbing.com/faucet-pricing",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "areaServed": [
          { "@type": "City", "name": "Denver" },
          { "@type": "AdministrativeArea", "name": "Denver Metro" }
        ]
      },
      {
        "@type": "Product", "name": "Standard-Grade Kitchen Faucet Installation Labor",
        "description": "Our $225 flat-rate labor service for installing a standard-grade kitchen faucet (priced under $250). This includes removal of the old faucet, installation of the new one, and full testing. The cost of the faucet is separate.",
        "sku": "FAUCET-INSTALL-STD",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/faucet-pricing#faucet-pricing-details", "priceCurrency": "USD", "price": "225", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Mid-Grade Kitchen Faucet Installation Labor",
        "description": "Our $325 flat-rate labor service for installing a mid-grade kitchen faucet (priced between $251 and $450). This service covers the more complex installation often required for these models. The cost of the faucet is separate.",
        "sku": "FAUCET-INSTALL-MID",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/faucet-pricing#faucet-pricing-details", "priceCurrency": "USD", "price": "325", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "High-Grade Kitchen Faucet Installation Labor",
        "description": "Our $485 flat-rate labor service for installing a high-grade kitchen faucet (priced between $451 and $1,000). This covers the precise and careful installation these premium fixtures require. The cost of the faucet is separate.",
        "sku": "FAUCET-INSTALL-HIGH",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/faucet-pricing#faucet-pricing-details", "priceCurrency": "USD", "price": "485", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Kitchen Faucet Repair Service",
        "description": "A flat-rate labor charge of $225 for diagnosing and repairing any kitchen faucet issue, such as leaks, drips, or cartridge failures. The cost of any required parts is additional and will be quoted upfront.",
        "sku": "FAUCET-REPAIR-LABOR",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/faucet-pricing#faucet-pricing-details", "priceCurrency": "USD", "price": "225", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Kitchen Faucet Supply Line Replacement",
        "description": "Our $225 flat-rate service includes the replacement of a pair of kitchen faucet supply lines with high-quality, durable braided stainless steel lines to prevent leaks.",
        "sku": "FAUCET-SUPPLY-LINE",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/faucet-pricing#faucet-pricing-details", "priceCurrency": "USD", "price": "225", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question", "name": "How much is the labor to install a kitchen faucet in Denver?",
            "acceptedAnswer": { "@type": "Answer", "text": "Colorado First Plumbing offers transparent, flat-rate labor costs for kitchen faucet installation in the Denver area. Labor for a standard-grade faucet is $225, mid-grade is $325, and high-grade is $485. The cost of the faucet itself is separate." }
          },
          {
            "@type": "Question", "name": "What is included in the installation labor price?",
            "acceptedAnswer": { "@type": "Answer", "text": "Our flat-rate labor price includes the professional removal and disposal of your old faucet, the careful installation of your new faucet, connection to the existing water supply lines, and a complete leak and functionality test." }
          },
          {
            "@type": "Question", "name": "What is the cost to repair a leaking kitchen faucet?",
            "acceptedAnswer": { "@type": "Answer", "text": "Our labor cost for any kitchen faucet repair is a flat-rate of $225, plus the cost of any necessary parts (like cartridges or hoses). We will always provide the total cost for your approval before beginning any work." }
          },
          {
            "@type": "Question", "name": "How do you classify a faucet as Standard, Mid-Grade, or High-Grade?",
            "acceptedAnswer": { "@type": "Answer", "text": "We classify faucets based on their retail price to determine the labor tier. Standard-grade faucets cost up to $250, Mid-grade from $251 to $450, and High-grade from $451 to $1,000. Faucets over $1,000 may require a custom quote due to their complexity." }
          }
        ]
      }
    ]
  };

  useEffect(() => {
    // Auto-advance hero gallery every 5 seconds
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % faucetProjects.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [faucetProjects.length]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % faucetProjects.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + faucetProjects.length) % faucetProjects.length);
  };

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-950">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-48 pb-16 bg-gradient-to-br from-blue-800 via-blue-700 to-blue-600">
        <div className="container mx-auto px-4 text-center relative z-10 py-16 md:py-24">
          <div className="inline-block px-6 py-3 mb-8 rounded-full bg-action-primary/90 text-white font-bold text-lg">
            🔧 Kitchen Faucet Specialists • All Grades Available • Professional Installation
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white leading-tight">
            Denver Kitchen Faucet Installation Cost
            <span className="block text-2xl md:text-3xl text-blue-200 font-semibold mt-2">
              See Exact Labor Prices Online
            </span>
          </h1>
          
          <p className="text-lg text-blue-100 mb-8">
            Master Plumber Owned • 360 Google Reviews • Professional Installation with Photo Proof
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            
            <a 
              href="#faucet-pricing-details" 
              className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
            >
              💰 See All Pricing Below
            </a>
          </div>
        </div>
      </section>

      {/* Trust & Authority Section */}
      <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
            Why Denver Metro Trusts Our Kitchen Faucet Installation Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
              <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
              <p className="text-blue-100 text-sm">Since 2018 serving Littleton, Denver Metro, and surrounding areas</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">360</div>
              <div className="text-lg font-semibold text-white mb-2">Google Reviews</div>
              <p className="text-blue-100 text-sm">4.8★ rating with verified customer feedback</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
              <div className="text-lg font-semibold text-white mb-2">Jurisdictions</div>
              <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities and counties</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
              <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
              <p className="text-blue-100 text-sm">Accredited business with excellent track record</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
              <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
              <p className="text-blue-100 text-sm">Showing Exact Labor Prices Online with 360 Google Reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* Proof of Work Gallery Section */}
      <section className="py-16 bg-gradient-to-br from-neutral-white to-trust-light/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-success-primary text-white font-bold text-lg">
              🏆 Real Projects • Real Results • Real Customers
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-authority-navy">
              See Our Professional Kitchen Faucet Installations
            </h2>
            <p className="text-xl text-neutral-gray mb-4">
              Colorado First Plumbing kitchen faucet services across Denver Metro • Professional documentation • Expert craftsmanship
            </p>
          </div>

          {/* Hero Gallery Carousel */}
          <div className="relative mb-12 max-w-5xl mx-auto">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-trust-primary/20">
              <img
                src={faucetProjects[currentImageIndex].image}
                alt={`${faucetProjects[currentImageIndex].type} installation in ${faucetProjects[currentImageIndex].location} - Colorado First Plumbing`}
                className="w-full h-full object-cover transition-all duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/80 via-transparent to-transparent"></div>
              
              {/* Project Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between">
                  <div className="mb-4 md:mb-0">
                    <div className="flex items-center mb-2">
                      <MapPin size={20} className="text-success-eco mr-2" />
                      <span className="text-success-light font-semibold">{faucetProjects[currentImageIndex].location}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-2">
                      {faucetProjects[currentImageIndex].type}
                    </h3>
                    <p className="text-lg text-trust-light mb-3">
                      {faucetProjects[currentImageIndex].description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {faucetProjects[currentImageIndex].features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-success-primary/90 rounded-full text-sm font-medium"
                        >
                          ✓ {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-success-eco mb-1">
                      {currentImageIndex + 1} / {faucetProjects.length}
                    </div>
                    <div className="text-sm text-trust-light">Professional Projects</div>
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-authority-navy p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
                aria-label="Previous image"
              >
                <ChevronLeft size={24} className="group-hover:scale-110 transition-transform" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-authority-navy p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
                aria-label="Next image"
              >
                <ChevronRight size={24} className="group-hover:scale-110 transition-transform" />
              </button>
            </div>

            {/* Dots Navigation */}
            <div className="flex justify-center mt-6 space-x-2">
              {faucetProjects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentImageIndex
                      ? 'bg-success-primary scale-125'
                      : 'bg-neutral-border hover:bg-trust-primary'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Project Grid Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
            {faucetProjects.map((project, index) => (
              <div
                key={project.id}
                className={`group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 ${
                  index === currentImageIndex 
                    ? 'border-success-primary ring-4 ring-success-primary/30 scale-105' 
                    : 'border-neutral-border hover:border-trust-primary'
                }`}
                onClick={() => {
                  setCurrentImageIndex(index);
                  openLightbox(index);
                }}
              >
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={`${project.type} installation in ${project.location} - Colorado First Plumbing`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <div className="flex items-center mb-1">
                      <MapPin size={14} className="text-success-eco mr-1" />
                      <span className="text-xs font-semibold">{project.location}</span>
                    </div>
                    <h4 className="text-sm font-bold leading-tight">{project.type}</h4>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-success-primary to-success-eco rounded-2xl p-8 shadow-xl border border-success-primary/20 max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                Ready to Join Our Satisfied Customers?
              </h3>
              <p className="text-lg mb-6 text-success-light">
                See your exact kitchen faucet labor cost below • Professional installation guaranteed • Same day service available
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a
                  href="#faucet-pricing-details"
                  className="bg-gradient-to-r from-trust-primary to-trust-deep hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-lg font-semibold inline-block transition-all duration-300"
                >
                  💰 See Pricing Below
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Pricing Section */}
      <section id="faucet-pricing-details" className="py-20 bg-neutral-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-authority-navy">
              Transparent Kitchen Faucet Pricing
            </h2>
            <p className="text-xl text-neutral-gray mb-4">
              See actual kitchen faucet installation labor costs with no hidden fees or surprise charges
            </p>
            <div className="inline-flex items-center bg-success-light px-6 py-3 rounded-full border-2 border-success-primary/20">
              <Shield className="w-6 h-6 text-success-primary mr-3" />
              <span className="text-success-primary font-bold">Kitchen Faucet Specialists • All Labor Costs Online</span>
            </div>
          </div>

          {/* Kitchen Faucet Installation Services */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-trust-light to-success-light/30 rounded-2xl p-8 border-2 border-trust-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Droplets size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Kitchen Faucet Installation Services</h3>
                  <p className="text-lg text-neutral-gray">Professional installation labor + your choice of faucet and materials</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Pricing Table */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-trust-primary to-trust-deep text-white p-4">
                      <h4 className="text-xl font-bold text-center">Kitchen Faucet Installation Labor</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="bg-success-light/50 p-4 rounded-lg border border-success-primary/20 mb-4">
                        <h5 className="font-bold text-success-primary mb-2">Faucet Grade Classifications:</h5>
                        <ul className="text-sm text-success-primary space-y-1">
                          <li>• <strong>Standard:</strong> $250 or under</li>
                          <li>• <strong>Mid-Grade:</strong> $251 to $450</li>
                          <li>• <strong>High-Grade:</strong> $451 to $1,000</li>
                          <li>• <strong>Premium:</strong> Over $1,000 (additional cost possible)</li>
                        </ul>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Standard-Grade Installation Labor</span>
                        <span className="text-xl font-bold text-success-primary">$225</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Mid-Grade Installation Labor</span>
                        <span className="text-xl font-bold text-success-primary">$325</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">High-Grade Installation Labor</span>
                        <span className="text-xl font-bold text-success-primary">$485</span>
                      </div>
                      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mt-4">
                        <p className="text-yellow-800 font-semibold text-sm">Plus: Cost of faucet and any materials needed</p>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Installation Includes</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Professional removal of old faucet
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Installation of new kitchen faucet
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Connection to existing plumbing
                        </li>
                                              </ul>
                    </div>
                    
                    {/* Exclusions Section */}
                    <div className="bg-red-50 p-4 border-t border-red-200">
                      <p className="text-red-800 font-semibold text-sm mb-2">*Installation Exclusions:</p>
                      <ul className="text-red-700 text-xs space-y-1">
                        <li>• Drilling holes in countertop</li>
                        <li>• Pot-filler type faucets</li>
                        <li>• Anything other than standard kitchen faucets</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Photo Gallery */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/existing-projects/best-plumbing-company-highlands-ranch.png"
                        alt="Kitchen faucet installation Highlands Ranch Colorado"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          High-Grade Kitchen Faucet - Highlands Ranch
                        </h4>
                        <p className="text-sm text-trust-light">
                          Professional installation with enhanced features and expert craftsmanship
                        </p>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-authority-navy mb-2">Professional Kitchen Faucet Installation</h4>
                      <ul className="space-y-2 text-sm text-neutral-gray">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Expert installation for all faucet grades
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Professional plumbing connections
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Complete testing and quality guarantee
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Kitchen Faucet Repair Services */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-action-warm/10 to-success-light/30 rounded-2xl p-8 border-2 border-action-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Wrench size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Kitchen Faucet Repair Services</h3>
                  <p className="text-lg text-neutral-gray">Professional kitchen faucet repairs to fix leaks, drips, and operational issues</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Pricing Table */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-action-primary to-action-urgent text-white p-4">
                      <h4 className="text-xl font-bold text-center">Kitchen Faucet Repair Pricing</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="bg-action-warm/10 p-4 rounded-lg border border-action-primary/20 mb-4">
                        <h5 className="font-bold text-action-primary mb-2">All Kitchen Faucet Repairs:</h5>
                        <div className="text-action-primary">
                          <p className="text-xl font-bold">$225 + Materials</p>
                          <p className="text-sm mt-1">Includes labor for any kitchen faucet repair</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Kitchen Faucet Leak Repair</span>
                        <span className="text-xl font-bold text-success-primary">$225 + Parts</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Cartridge Replacement</span>
                        <span className="text-xl font-bold text-success-primary">$225 + Parts</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Pull-Out Hose Replacement</span>
                        <span className="text-xl font-bold text-success-primary">$225 + Parts</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Handle Repair/Replacement</span>
                        <span className="text-xl font-bold text-success-primary">$225 + Parts</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Side Sprayer Replacement</span>
                        <span className="text-xl font-bold text-success-primary">$225 + Parts</span>
                      </div>
                      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mt-4">
                        <p className="text-yellow-800 font-semibold text-sm">Special Ordering Fee:</p>
                        <p className="text-yellow-700 text-sm">$50 plus parts cost additional if parts must be ordered vs purchased at nearby store</p>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Repair Service Advantages</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Fixed price for all kitchen faucet repairs
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Professional diagnosis & repair
                        </li>
                                              </ul>
                    </div>
                  </div>
                </div>

                {/* Photo Gallery */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/existing-projects/gas-pipe-install-lakewood-colorado-first-plumbing.png"
                        alt="Kitchen faucet repair service Lakewood Colorado"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Expert Kitchen Faucet Repair - Lakewood
                        </h4>
                        <p className="text-sm text-trust-light">
                          Professional diagnosis and repair of kitchen faucet issues with quality parts
                        </p>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-authority-navy mb-2">Professional Kitchen Faucet Repair Process</h4>
                      <ul className="space-y-2 text-sm text-neutral-gray">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Complete diagnosis of kitchen faucet issues
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Quality parts replacement when needed
                        </li>
                                              </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Supply Line Services */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-success-light/50 to-trust-light/30 rounded-2xl p-8 border-2 border-success-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Settings size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Supply Line & Connection Services</h3>
                  <p className="text-lg text-neutral-gray">Professional supply line replacement and connection services</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Pricing Table */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-success-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-success-primary to-success-eco text-white p-4">
                      <h4 className="text-xl font-bold text-center">Supply Line Services</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Kitchen Faucet Supply Line Replacement (Pair)</span>
                        <span className="text-xl font-bold text-success-primary">$225</span>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Supply Line Service Includes</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          New braided stainless steel lines (up to 24")
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Removal of old leaking lines
                        </li>
                                              </ul>
                    </div>
                    
                    {/* Exclusions Section */}
                    <div className="bg-yellow-50 p-4 border-t border-yellow-200">
                      <p className="text-yellow-800 font-semibold text-sm mb-2">*Supply Line Service Exclusions:</p>
                      <ul className="text-yellow-700 text-xs space-y-1">
                        <li>• Faucet replacement or repair (separate service)</li>
                        <li>• Angle stop replacement if seized or leaking</li>
                        <li>• Issues within the faucet body itself</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Photo Gallery */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-success-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/services/residential/remodel-trailer.JPEG"
                        alt="Supply line replacement service Denver Metro Colorado"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Professional Supply Line Service - Denver Metro
                        </h4>
                        <p className="text-sm text-trust-light">
                          Quality braided stainless steel supply lines with professional installation
                        </p>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-authority-navy mb-2">Quality Supply Line Installation</h4>
                      <ul className="space-y-2 text-sm text-neutral-gray">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Durable braided stainless steel construction
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Professional installation and leak testing
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Prevents water damage and ensures reliability
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section for Schema Parity */}
          <section className="py-16 bg-gradient-to-br from-neutral-white to-trust-light/30">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-3xl md:text-5xl font-bold mb-12 text-authority-navy text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-md border">
                  <h3 className="font-bold text-lg text-authority-navy">How much is the labor to install a kitchen faucet in Denver?</h3>
                  <p className="text-neutral-gray mt-2">Colorado First Plumbing offers transparent, flat-rate labor costs for kitchen faucet installation in the Denver area. Labor for a standard-grade faucet is $225, mid-grade is $325, and high-grade is $485. The cost of the faucet itself is separate.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border">
                  <h3 className="font-bold text-lg text-authority-navy">What is included in the installation labor price?</h3>
                                  </div>
                <div className="bg-white p-6 rounded-lg shadow-md border">
                  <h3 className="font-bold text-lg text-authority-navy">What is the cost to repair a leaking kitchen faucet?</h3>
                  <p className="text-neutral-gray mt-2">Our labor cost for any kitchen faucet repair is a flat-rate of $225, plus the cost of any necessary parts (like cartridges or hoses). We will always provide the total cost for your approval before beginning any work.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border">
                  <h3 className="font-bold text-lg text-authority-navy">How do you classify a faucet as Standard, Mid-Grade, or High-Grade?</h3>
                  <p className="text-neutral-gray mt-2">We classify faucets based on their retail price to determine the labor tier. Standard-grade faucets cost up to $250, Mid-grade from $251 to $450, and High-grade from $451 to $1,000. Faucets over $1,000 may require a custom quote due to their complexity.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20">
              <h3 className="text-3xl font-bold mb-4">Ready for Professional Kitchen Faucet Service?</h3>
              <p className="text-xl mb-6 text-trust-light">
                See your exact labor cost above • No hidden fees • Master plumber owned • 360 Google Reviews
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="/garbage-disposal-pricing" 
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                >
                  🔧 See Garbage Disposal Pricing
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Lightbox Modal */}
      {selectedImage !== null && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={closeLightbox}>
          <div className="relative max-w-6xl w-full max-h-full">
            <img
              src={faucetProjects[selectedImage].image}
              alt={`${faucetProjects[selectedImage].type} installation in ${faucetProjects[selectedImage].location} - Colorado First Plumbing`}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-authority-navy p-2 rounded-full shadow-lg transition-all duration-200"
              aria-label="Close lightbox"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="absolute bottom-4 left-4 right-4 bg-black/80 rounded-lg p-4 text-white">
              <div className="flex items-center mb-2">
                <MapPin size={20} className="text-success-eco mr-2" />
                <span className="text-success-light font-semibold">{faucetProjects[selectedImage].location}</span>
              </div>
              <h3 className="text-xl font-bold mb-2">{faucetProjects[selectedImage].type}</h3>
              <p className="text-trust-light mb-3">{faucetProjects[selectedImage].description}</p>
              <div className="flex flex-wrap gap-2">
                {faucetProjects[selectedImage].features.map((feature, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-success-primary/90 rounded-full text-sm font-medium"
                  >
                    ✓ {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      
      {/* Schema Injection */}
      <SchemaInjector 
        schemas={[completePageSchema]}
        title="Denver Kitchen Faucet Installation Cost | Labor from $225 | Colorado First Plumbing"
        description="See exact, flat-rate labor costs for kitchen faucet installation in Denver. Standard-grade labor: $225. Mid-grade: $325. High-grade: $485. 360 Google Reviews."
        canonical="https://colorado-plumbing.com/faucet-pricing"
        keywords="denver kitchen faucet installation cost, faucet replacement labor price, plumber to install faucet denver, faucet repair cost"
      />
    </div>
  );
};

export default FaucetPricing;